<script lang="ts">
export default {
	name: "ChartAxis",
};
</script>

<script setup lang="ts">
import { propsFactory } from "@soft-db/soft-utils";
import {
	axisBottom,
	axisLeft,
	axisRight,
	axisTop,
	interpolateRound,
	scaleLinear,
	scaleLog,
	scaleTime,
	select,
	type Numeric,
} from "d3";
import { computed, onMounted, ref, watch, watchEffect, type PropType } from "vue";
import type {
	AxisGridOptions,
	AxisOptions,
	AxisOrientation,
	AxisRange,
	AxisScale,
	AxisSide,
	AxisTickOptions,
	AxisType,
} from "../interfaces";
import { createZoomTransform } from "../use/manual-zoom";
import { useScaleTimeFormat } from "../use/scale-time-format";
import { useSVGContainer } from "../use/svg-container";
import { createTzTicker } from "../use/tz-ticker";

const props = defineProps(
	propsFactory<AxisOptions>()({
		side: {
			type: String as PropType<AxisSide>,
			required: true,
		},
		type: {
			type: String as PropType<AxisType>,
			required: true,
		},
		visible: Boolean,
		range: Object as PropType<AxisRange>,
		initialZoom: Object as PropType<AxisRange>,
		label: String,
		margin: Number,
		grid: Object as PropType<AxisGridOptions>,
		ticks: Object as PropType<AxisTickOptions>,
		gesture: Boolean,
		animation: Boolean,
	})
);

const emit = defineEmits<{
	(e: "update:scale", scale: AxisScale): void;
	(e: "update:originalScale", scale: AxisScale): void;
	(e: "update:range", range: Numeric[]): void;
}>();

const { transform, setTransform, contentDimension } = useSVGContainer();

const axisType = computed<AxisOrientation>(() => {
	const { side } = props;

	if (side === "left" || side === "right") return "y";

	return "x";
});

const axisGroup = ref<SVGGElement>();

const axisAttrs = computed(() => {
	const { side } = props;
	const { width, height } = contentDimension.value;

	let transform: string | undefined;
	if (side === "right") {
		transform = `translate(${width}, 0)`;
	} else if (side === "bottom") {
		transform = `translate(0, ${height})`;
	}

	return {
		transform,
	};
});

const labelAttrs = computed(() => {
	const { side, margin } = props;
	const { width, height } = contentDimension.value;

	let transform: string | undefined;
	if (side === "bottom") {
		transform = `translate(${width / 2}, ${height + 40 + (margin || 0)})`;
	} else if (side === "left") {
		transform = `translate(-${margin || 40}, ${height / 2}) rotate(-90)`;
	} else if (side === "right") {
		transform = `translate(${width + 30 + (margin || 0)}, ${height / 2}) rotate(90)`;
	}

	return {
		transform,
	};
});

const scaleRange = computed<number[]>(() => {
	const { width, height } = contentDimension.value;

	if (axisType.value === "x") return [0, width];
	else return [height, 0];
});

const stopRangeWatch = watch(scaleRange, () => {
	if (scaleRange.value[0] === 0 && scaleRange.value[1] === 0) {
		return;
	}

	if (props.initialZoom != null && props.range != null) {
		setTransform(
			createZoomTransform({
				side: props.side,
				range: props.range,
				zoom: props.initialZoom,
				scale: scale.value,
				transform: transform.value,
			})
		);
	}

	stopRangeWatch();
});

const scaleDomain = computed<Numeric[]>(() => {
	const { range } = props;

	let [min, max]: [Numeric, Numeric] = [0, 0];
	if (range) {
		min = range.min;
		max = range.max;
	}

	if (props.type === "log" && min.valueOf() <= 0) {
		min = 0.1;
	}

	return [min, max];
});

const { tickInterval: tzTickInterval } = createTzTicker(props.ticks?.timezone);

const originalScale = computed<AxisScale>(() => {
	const { type } = props;

	let scale: AxisScale;
	switch (type) {
		case "linear":
			scale = scaleLinear();
			break;
		case "log":
			scale = scaleLog();
			break;
		case "time":
			scale = scaleTime();
			break;
	}

	scale.range(scaleRange.value);
	scale.domain(scaleDomain.value);
	return scale;
});
watch(
	originalScale,
	(scale) => {
		emit("update:originalScale", scale);
	},
	{ immediate: true }
);

onMounted(() => {
	if (props.range != null && props.initialZoom != null) {
		setTransform(
			createZoomTransform({
				side: props.side,
				range: props.range,
				zoom: props.initialZoom,
				scale: originalScale.value,
				transform: transform.value,
			})
		);
	}
});

const scale = computed<AxisScale>(() => {
	if (!props.gesture) return originalScale.value;

	let rescale: (axis: AxisScale) => AxisScale;
	if (axisType.value === "x") {
		rescale = transform.value.rescaleX.bind(transform.value);
	} else {
		rescale = transform.value.rescaleY.bind(transform.value);
	}

	return rescale(originalScale.value).interpolate(interpolateRound) as AxisScale;
});

watch(
	scale,
	(scale, previous) => {
		emit("update:scale", scale);

		if (!previous) return;

		const [start, end] = scale.domain();
		const [previousStart, previousEnd] = previous.domain();

		if (start.valueOf() === previousStart.valueOf() && end.valueOf() === previousEnd.valueOf())
			return;

		emit("update:range", scale.domain());
	},
	{ immediate: true }
);

const axisConstructor = computed(() => {
	const { side } = props;

	switch (side) {
		case "left":
			return axisLeft;
		case "top":
			return axisTop;
		case "right":
			return axisRight;
		case "bottom":
			return axisBottom;
		default:
			return axisBottom;
	}
});
const numberOfTicks = computed(() => {
	const { width, height } = contentDimension.value;

	if ((axisType.value === "x" && width < 400) || (axisType.value === "y" && height < 400)) {
		return 2;
	}
	if ((axisType.value === "x" && width < 600) || (axisType.value === "y" && height < 600)) {
		return 4;
	}

	return props.ticks?.count || 11;
});

const { formatTime } = useScaleTimeFormat();

const timeFormat = (d: Numeric) => {
	return formatTime(d, props.ticks?.timezone || "", props.ticks?.locale || "");
};

const axisFormat = computed(() => {
	if (props.type === "time" && !props.ticks?.format) return timeFormat;
	return props.ticks?.format;
});

const axis = computed(() => {
	const axis = axisConstructor.value(scale.value);
	axis.tickSize(10).tickPadding(10);

	if (props.ticks?.values) {
		axis.tickValues(props.ticks.values);
	} else {
		if (typeof axisFormat.value === "function") {
			if (props.type === "time") {
				const d = scale.value.domain();
				axis
					.ticks(tzTickInterval(d[0], d[d.length - 1], numberOfTicks.value))
					.tickFormat(axisFormat.value);
			} else {
				axis.ticks(numberOfTicks.value).tickFormat(axisFormat.value);
			}
		} else {
			axis.ticks(numberOfTicks.value, props.ticks?.format);
		}
	}

	return axis;
});

const tickSize = computed(() => {
	const { grid, visible } = props;

	if (!visible && !grid?.visible) return 0;
	if (!grid?.visible) return 6;

	const { width, height } = contentDimension.value;
	if (axisType.value === "x") return -height;
	else return -width;
});

watchEffect(() => {
	if (!axisGroup.value) return;

	axis.value.tickSize(tickSize.value);

	// *Draw the axis
	let selection = select(axisGroup.value);
	if (props.animation) {
		selection = selection.transition() as any;
	}
	selection.call(axis.value);
	selection.selectAll(".tick text").each(function () {
		const text = select(this);
		const words = text.text().split("#");
		if (words.length === 1) return;

		let y = +text.attr("y");
		const dy = text.attr("dy");

		text.text(null);
		for (const word of words) {
			text.append("tspan").attr("x", 0).attr("y", y).attr("dy", dy).text(word) as any;

			y += 16;
		}
	});
});
</script>

<template>
	<g>
		<g
			ref="axisGroup"
			class="svg-axis"
			:class="`${!visible ? 'hide-tick' : ''}`"
			v-bind="axisAttrs"
		/>
		<text class="axis-label" v-bind="labelAttrs">{{ label }}</text>
	</g>
</template>

<style>
.svg-axis .domain {
	display: none !important;
}

.svg-axis.hide-tick .tick text {
	opacity: 0;
}

.svg-axis .tick text {
	font-size: 0.75rem;
	user-select: none;
}

.svg-axis .tick text {
	color: var(--color-base-content);
}

.svg-axis .tick line {
	color: color-mix(in oklab, var(--color-card), black 15%);

	&:where([data-theme="dark"] *) {
		color: color-mix(in oklab, var(--color-card), white 15%);
	}
}

.axis-label {
	/* @apply text-base-content fill-current font-sans text-sm; */
	fill: currentColor;
	color: var(--color-base-content);
	font-size: 0.875rem;
	text-anchor: middle;
}
</style>
